import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  CalendarDays,
  Copy,
  Loader2,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Truck,
  X,
} from "lucide-react";

import i18next from "i18next";
import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";
import VehicleTypePicker from "./vehicle/VehicleTypePicker.jsx";
import { vehicleTypeLabel } from "./vehicle/vehicleCatalog.js";

const COPY = {
  ru: {
    title: "Мои грузы",
    add: "Добавить груз",
    active: "Активные",
    history: "История",
    loading: "Загрузка грузов…",
    loadError: "Не удалось загрузить грузы.",
    emptyActive: "Активных грузов пока нет.",
    emptyHistory: "История пока пуста.",
    edit: "Редактировать",
    close: "Завершить",
    repeat: "Повторить",
    closing: "Завершаем…",
    closeConfirm: "Завершить публикацию этого груза?",
    closeError: "Не удалось завершить публикацию.",
    expires: "Актуально до",
    closed: "Завершено",
    expired: "Срок истёк",
    blocked: "Заблокировано",
    loadingDate: "Загрузка",
    ready: "Готов к загрузке",
    negotiable: "Цена договорная",
    weight: "Вес",
    volume: "Объём",
    trucks: "Машин",
    refresh: "Обновить",
    back: "Назад",
    tons: "т",
    cubic: "м³",
    ttl: "Объявление активно 12 часов. Редактирование обновляет срок ещё на 12 часов.",
    modalCreate: "Новый груз",
    modalEdit: "Редактировать груз",
    modalRepeat: "Повторить груз",
    modalCreateSub: "Заполните данные для публикации",
    targetedVehicle: "После публикации груз будет сразу отправлен машине",
    modalEditSub: "Измените параметры активного груза",
    modalRepeatSub: "Создастся новое объявление на 12 часов",
    from: "Откуда",
    to: "Куда",
    cargo: "Груз",
    weightTons: "Вес, т",
    volumeM3: "Объём, м³",
    truckType: "Тип машины",
    requiredTrucks: "Количество машин",
    date: "Дата загрузки",
    readyNow: "Груз готов сейчас",
    priceMode: "Цена",
    fixed: "Фиксированная",
    priceAmount: "Сумма",
    currency: "Валюта",
    advance: "Аванс, %",
    payment: "Оплата",
    comment: "Комментарий",
    publish: "Опубликовать",
    save: "Сохранить",
    publishing: "Публикуем…",
    saving: "Сохраняем…",
    cancel: "Отмена",
    paymentNegotiable: "Договорная",
    paymentCash: "Наличные",
    paymentBank: "Безналичный расчёт",
    paymentCard: "Карта",
    paymentOther: "Другое",
    routeRequired: "Укажите откуда и куда.",
    cargoRequired: "Укажите груз.",
    weightInvalid: "Укажите корректный вес.",
    truckRequired: "Выберите тип машины.",
    loadingRequired: "Укажите дату загрузки или отметьте, что груз готов сейчас.",
    priceInvalid: "Укажите корректную стоимость.",
    trucksInvalid: "Укажите количество машин.",
    advanceInvalid: "Аванс должен быть от 0 до 100%.",
    sessionMissing: "Не найдена сессия водителя.",
    saveError: "Не удалось сохранить груз.",
  },
  uz: {
    title: "Менинг юкларим",
    add: "Юк қўшиш",
    active: "Фаол",
    history: "Тарих",
    loading: "Юклар юкланмоқда…",
    loadError: "Юкларни юклаб бўлмади.",
    emptyActive: "Ҳозирча фаол юк йўқ.",
    emptyHistory: "Тарих ҳозирча бўш.",
    edit: "Таҳрирлаш",
    close: "Якунлаш",
    repeat: "Такрорлаш",
    closing: "Якунланмоқда…",
    closeConfirm: "Бу юк эълонини якунлайсизми?",
    closeError: "Эълонни якунлаб бўлмади.",
    expires: "Амал қилади",
    closed: "Якунланган",
    expired: "Муддати ўтган",
    blocked: "Блокланган",
    loadingDate: "Юклаш",
    ready: "Юклашга тайёр",
    negotiable: "Нарх келишилади",
    weight: "Оғирлик",
    volume: "Ҳажм",
    trucks: "Машина",
    refresh: "Янгилаш",
    back: "Орқага",
    tons: "т",
    cubic: "м³",
    ttl: "Эълон 12 соат фаол туради. Таҳрирланса, муддат яна 12 соатга янгиланади.",
    modalCreate: "Янги юк",
    modalEdit: "Юкни таҳрирлаш",
    modalRepeat: "Юкни такрорлаш",
    modalCreateSub: "Эълон учун маълумотларни киритинг",
    targetedVehicle: "Эълондан кейин юк танланган машинага дарҳол юборилади",
    modalEditSub: "Фаол юк маълумотларини ўзгартиринг",
    modalRepeatSub: "12 соатга янги эълон яратилади",
    from: "Қаердан",
    to: "Қаерга",
    cargo: "Юк",
    weightTons: "Оғирлик, т",
    volumeM3: "Ҳажм, м³",
    truckType: "Машина тури",
    requiredTrucks: "Машиналар сони",
    date: "Юклаш санаси",
    readyNow: "Юк ҳозир тайёр",
    priceMode: "Нарх",
    fixed: "Белгиланган",
    priceAmount: "Сумма",
    currency: "Валюта",
    advance: "Аванс, %",
    payment: "Тўлов",
    comment: "Изоҳ",
    publish: "Эълон бериш",
    save: "Сақлаш",
    publishing: "Эълон берилмоқда…",
    saving: "Сақланмоқда…",
    cancel: "Бекор қилиш",
    paymentNegotiable: "Келишилади",
    paymentCash: "Нақд",
    paymentBank: "Банк ўтказмаси",
    paymentCard: "Карта",
    paymentOther: "Бошқа",
    routeRequired: "Қаердан ва қаерга эканини киритинг.",
    cargoRequired: "Юк номини киритинг.",
    weightInvalid: "Оғирликни тўғри киритинг.",
    truckRequired: "Машина турини танланг.",
    loadingRequired: "Юклаш санасини киритинг ёки юк ҳозир тайёрлигини белгиланг.",
    priceInvalid: "Нархни тўғри киритинг.",
    trucksInvalid: "Машиналар сонини киритинг.",
    advanceInvalid: "Аванс 0 дан 100% гача бўлиши керак.",
    sessionMissing: "Ҳайдовчи сессияси топилмади.",
    saveError: "Юкни сақлаб бўлмади.",
  },
  tr: {
    title: "Yüklerim",
    add: "Yük ekle",
    active: "Aktif",
    history: "Geçmiş",
    loading: "Yükler yükleniyor…",
    loadError: "Yükler yüklenemedi.",
    emptyActive: "Aktif yük yok.",
    emptyHistory: "Geçmiş henüz boş.",
    edit: "Düzenle",
    close: "Bitir",
    repeat: "Tekrarla",
    closing: "Bitiriliyor…",
    closeConfirm: "Bu yük ilanını bitirmek istiyor musunuz?",
    closeError: "İlan bitirilemedi.",
    expires: "Geçerli",
    closed: "Bitti",
    expired: "Süresi doldu",
    blocked: "Engellendi",
    loadingDate: "Yükleme",
    ready: "Yüklemeye hazır",
    negotiable: "Fiyat görüşülür",
    weight: "Ağırlık",
    volume: "Hacim",
    trucks: "Araç",
    refresh: "Yenile",
    back: "Geri",
    tons: "t",
    cubic: "m³",
    ttl: "İlan 12 saat aktiftir. Düzenleme süresini yeniden 12 saate çıkarır.",
    modalCreate: "Yeni yük",
    modalEdit: "Yükü düzenle",
    modalRepeat: "Yükü tekrarla",
    modalCreateSub: "İlan bilgilerini doldurun",
    targetedVehicle: "Yayınlandıktan sonra yük seçilen araca hemen gönderilecek",
    modalEditSub: "Aktif yük bilgilerini değiştirin",
    modalRepeatSub: "12 saatlik yeni bir ilan oluşturulur",
    from: "Nereden",
    to: "Nereye",
    cargo: "Yük",
    weightTons: "Ağırlık, t",
    volumeM3: "Hacim, m³",
    truckType: "Araç tipi",
    requiredTrucks: "Araç sayısı",
    date: "Yükleme tarihi",
    readyNow: "Yük şimdi hazır",
    priceMode: "Fiyat",
    fixed: "Sabit",
    priceAmount: "Tutar",
    currency: "Para birimi",
    advance: "Avans, %",
    payment: "Ödeme",
    comment: "Not",
    publish: "Yayınla",
    save: "Kaydet",
    publishing: "Yayınlanıyor…",
    saving: "Kaydediliyor…",
    cancel: "İptal",
    paymentNegotiable: "Görüşülür",
    paymentCash: "Nakit",
    paymentBank: "Banka havalesi",
    paymentCard: "Kart",
    paymentOther: "Diğer",
    routeRequired: "Nereden ve nereye olduğunu yazın.",
    cargoRequired: "Yükü yazın.",
    weightInvalid: "Geçerli bir ağırlık yazın.",
    truckRequired: "Araç tipi seçin.",
    loadingRequired: "Yükleme tarihi seçin veya yükün şimdi hazır olduğunu işaretleyin.",
    priceInvalid: "Geçerli bir fiyat yazın.",
    trucksInvalid: "Araç sayısını yazın.",
    advanceInvalid: "Avans %0 ile %100 arasında olmalı.",
    sessionMissing: "Sürücü oturumu bulunamadı.",
    saveError: "Yük kaydedilemedi.",
  },
};

const TRUCK_TYPE_ALIASES = {
  REFRIGERATOR: "REF",
  VAN: "BOX",
  FLATBED: "PLATFORM",
};

function normalizeTruckType(value) {
  const raw = text(value).toUpperCase();
  return TRUCK_TYPE_ALIASES[raw] || raw;
}

function text(value) {
  return String(value == null ? "" : value).trim();
}

function languageCode() {
  const raw = text(i18next.language).toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

function bearer(token) {
  const value = text(token);
  if (!value) return "";
  return /^bearer\s+/i.test(value) ? value : `Bearer ${value}`;
}

function formatDateTime(value, lang) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const locale = lang === "uz" ? "uz-UZ" : lang === "tr" ? "tr-TR" : "ru-RU";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatDateOnly(value, lang) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return text(value) || "—";
  const locale = lang === "uz" ? "uz-UZ" : lang === "tr" ? "tr-TR" : "ru-RU";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatAmount(value, currency, lang) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  const locale = lang === "uz" ? "uz-UZ" : lang === "tr" ? "tr-TR" : "ru-RU";
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(amount)} ${text(currency)}`;
}

function itemTime(item) {
  const raw = item?.closedAt || item?.updatedAt || item?.createdAt || item?.expiresAt;
  const ms = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(ms) ? ms : 0;
}

function todayDate() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function freshForm(item = null) {
  return {
    from: text(item?.from),
    to: text(item?.to),
    cargo: text(item?.cargo),
    weightTons: item?.weightTons == null ? "" : String(item.weightTons),
    volumeM3: item?.volumeM3 == null ? "" : String(item.volumeM3),
    truckTypes: Array.isArray(item?.truckTypes) && item.truckTypes.length
      ? Array.from(new Set(item.truckTypes.map(normalizeTruckType).filter(Boolean))).slice(0, 8)
      : ["TENT"],
    requiredTrucks: String(item?.requiredTrucks || 1),
    loadingDate: text(item?.loadingDate) || todayDate(),
    loadingReady: Boolean(item?.loadingReady),
    priceMode: text(item?.priceMode).toUpperCase() === "FIXED" ? "FIXED" : "NEGOTIABLE",
    priceAmount: item?.priceAmount == null ? "" : String(item.priceAmount),
    currency: text(item?.currency).toUpperCase() || "UZS",
    advancePercent: item?.advancePercent == null ? "0" : String(item.advancePercent),
    paymentType: text(item?.paymentType).toUpperCase() || "NEGOTIABLE",
    comment: text(item?.comment),
  };
}

function backendMessage(code, t) {
  const map = {
    ROUTE_REQUIRED: t.routeRequired,
    ROUTE_SAME_POINT: t.routeRequired,
    CARGO_REQUIRED: t.cargoRequired,
    BAD_WEIGHT: t.weightInvalid,
    BAD_VOLUME: t.weightInvalid,
    TRUCK_TYPE_REQUIRED: t.truckRequired,
    BAD_TRUCK_COUNT: t.trucksInvalid,
    LOADING_REQUIRED: t.loadingRequired,
    BAD_LOADING_DATE: t.loadingRequired,
    BAD_PRICE: t.priceInvalid,
    BAD_ADVANCE: t.advanceInvalid,
    POST_NOT_ACTIVE: t.saveError,
    POST_NOT_FOUND: t.saveError,
  };
  return map[code] || code || t.saveError;
}

function FreightModal({ open, onClose, driverToken, item, mode, onSaved, targetLabel = "" }) {
  const lang = languageCode();
  const t = COPY[lang] || COPY.ru;
  const [form, setForm] = useState(() => freshForm(item));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(freshForm(item));
    setBusy(false);
    setError("");
  }, [open, item?.id, item?.updatedAt, mode]);

  if (!open) return null;

  const editing = mode === "edit" && text(item?.id);
  const title = editing ? t.modalEdit : mode === "repeat" ? t.modalRepeat : t.modalCreate;
  const subtitle = editing ? t.modalEditSub : mode === "repeat" ? t.modalRepeatSub : t.modalCreateSub;
  const patch = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError("");

    const from = text(form.from);
    const to = text(form.to);
    const cargo = text(form.cargo);
    const weightTons = Number(form.weightTons);
    const volumeM3 = text(form.volumeM3) === "" ? null : Number(form.volumeM3);
    const requiredTrucks = Number(form.requiredTrucks);
    const advancePercent = text(form.advancePercent) === "" ? 0 : Number(form.advancePercent);

    if (from.length < 2 || to.length < 2 || from.toLowerCase() === to.toLowerCase()) {
      return setError(t.routeRequired);
    }
    if (cargo.length < 2) return setError(t.cargoRequired);
    if (!Number.isFinite(weightTons) || weightTons < 0.1 || weightTons > 1000) {
      return setError(t.weightInvalid);
    }
    if (volumeM3 !== null && (!Number.isFinite(volumeM3) || volumeM3 < 0 || volumeM3 > 10000)) {
      return setError(t.weightInvalid);
    }
    if (!Array.isArray(form.truckTypes) || !form.truckTypes.length) {
      return setError(t.truckRequired);
    }
    if (!Number.isInteger(requiredTrucks) || requiredTrucks < 1 || requiredTrucks > 1000) {
      return setError(t.trucksInvalid);
    }
    if (!form.loadingReady && !text(form.loadingDate)) {
      return setError(t.loadingRequired);
    }
    if (!Number.isFinite(advancePercent) || advancePercent < 0 || advancePercent > 100) {
      return setError(t.advanceInvalid);
    }

    let priceAmount = null;
    if (form.priceMode === "FIXED") {
      priceAmount = Number(form.priceAmount);
      if (!Number.isFinite(priceAmount) || priceAmount <= 0) {
        return setError(t.priceInvalid);
      }
    }

    const auth = bearer(driverToken);
    if (!auth) return setError(t.sessionMissing);

    setBusy(true);
    try {
      const endpoint = editing
        ? `/api/driver/own-freights/${encodeURIComponent(text(item.id))}`
        : "/api/driver/own-freights";

      const response = await fetch(endpoint, {
        method: editing ? "PATCH" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth,
        },
        body: JSON.stringify({
          from,
          to,
          cargo,
          weightTons,
          volumeM3,
          truckTypes: form.truckTypes,
          requiredTrucks,
          loadingDate: form.loadingReady ? "" : text(form.loadingDate),
          loadingReady: Boolean(form.loadingReady),
          priceMode: form.priceMode,
          priceAmount,
          currency: text(form.currency).toUpperCase() || "UZS",
          advancePercent,
          paymentType: form.paymentType,
          comment: text(form.comment),
          ownerLocale: lang,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(backendMessage(text(data?.error || data?.message), t));
      }

      onSaved?.(data?.post || null, { mode: editing ? "edit" : mode === "repeat" ? "repeat" : "create" });
    } catch (err) {
      setError(text(err?.message || err) || t.saveError);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded-2xl border border-slate-700 bg-slate-950/70 px-3 py-3 text-sm text-white outline-none focus:border-blue-400";
  const label = "mb-1.5 block text-[13px] font-bold uppercase tracking-wide text-slate-400";

  return (
    <div className="fixed inset-0 z-[10090] flex items-end bg-black/65 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true">
      <div className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl border border-slate-700 bg-slate-900 shadow-2xl sm:max-w-2xl sm:rounded-3xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-700 bg-slate-900/95 px-4 py-4 backdrop-blur">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600"><Package size={22} /></div>
            <div className="min-w-0">
              <div className="truncate text-base font-black text-white">{title}</div>
              <div className="truncate text-[13px] text-slate-400">{subtitle}</div>
            </div>
          </div>
          <button type="button" disabled={busy} onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-800 text-white"><X size={19} /></button>
        </div>

        {targetLabel && !editing ? (
          <div className="mx-4 mt-4 rounded-2xl border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-[13px] font-bold text-blue-200">
            {t.targetedVehicle}: {targetLabel}
          </div>
        ) : null}

        <form onSubmit={submit} className="space-y-4 p-4 pb-7">
          <div className="grid grid-cols-2 gap-3">
            <label><span className={label}>{t.from}</span><input className={input} value={form.from} onChange={(e) => patch("from", e.target.value)} /></label>
            <label><span className={label}>{t.to}</span><input className={input} value={form.to} onChange={(e) => patch("to", e.target.value)} /></label>
          </div>

          <label><span className={label}>{t.cargo}</span><input className={input} value={form.cargo} onChange={(e) => patch("cargo", e.target.value)} /></label>

          <div className="grid grid-cols-2 gap-3">
            <label><span className={label}>{t.weightTons}</span><input className={input} type="number" min="0.1" max="1000" step="0.1" inputMode="decimal" value={form.weightTons} onChange={(e) => patch("weightTons", e.target.value)} /></label>
            <label><span className={label}>{t.volumeM3}</span><input className={input} type="number" min="0" max="10000" step="0.1" inputMode="decimal" value={form.volumeM3} onChange={(e) => patch("volumeM3", e.target.value)} /></label>
          </div>

          <div>
            <span className={label}>{t.truckType}</span>
            <VehicleTypePicker
              value={form.truckTypes}
              onChange={(truckTypes) => patch("truckTypes", truckTypes)}
              domain="freight"
              multiple
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label><span className={label}>{t.requiredTrucks}</span><input className={input} type="number" min="1" max="1000" step="1" inputMode="numeric" value={form.requiredTrucks} onChange={(e) => patch("requiredTrucks", e.target.value)} /></label>
            <label><span className={label}>{t.date}</span><input className={input} type="date" disabled={form.loadingReady} value={form.loadingDate} onChange={(e) => patch("loadingDate", e.target.value)} /></label>
          </div>

          <label className="flex items-center gap-3 rounded-2xl border border-slate-700 bg-slate-950/50 p-3 text-sm font-bold text-slate-200">
            <input type="checkbox" checked={form.loadingReady} onChange={(e) => patch("loadingReady", e.target.checked)} className="h-5 w-5" />
            {t.readyNow}
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className={label}>{t.priceMode}</span>
              <NativeSafeSelect
                value={form.priceMode}
                onChange={(value) => patch("priceMode", value)}
                buttonClassName={input}
                placement="auto"
                ariaLabel={t.priceMode}
                options={[
                  { value: "NEGOTIABLE", label: t.negotiable },
                  { value: "FIXED", label: t.fixed },
                ]}
              />
            </label>
            <label>
              <span className={label}>{t.currency}</span>
              <NativeSafeSelect
                value={form.currency}
                onChange={(value) => patch("currency", value)}
                buttonClassName={input}
                placement="auto"
                ariaLabel={t.currency}
                options={['UZS','USD','RUB','KZT','EUR'].map((value) => ({ value, label: value }))}
              />
            </label>
          </div>

          {form.priceMode === "FIXED" && <label><span className={label}>{t.priceAmount}</span><input className={input} type="number" min="1" step="1" inputMode="decimal" value={form.priceAmount} onChange={(e) => patch("priceAmount", e.target.value)} /></label>}

          <div className="grid grid-cols-2 gap-3">
            <label><span className={label}>{t.advance}</span><input className={input} type="number" min="0" max="100" step="1" inputMode="decimal" value={form.advancePercent} onChange={(e) => patch("advancePercent", e.target.value)} /></label>
            <label>
              <span className={label}>{t.payment}</span>
              <NativeSafeSelect
                value={form.paymentType}
                onChange={(value) => patch("paymentType", value)}
                buttonClassName={input}
                placement="auto"
                ariaLabel={t.payment}
                options={[
                  { value: "NEGOTIABLE", label: t.paymentNegotiable },
                  { value: "CASH", label: t.paymentCash },
                  { value: "BANK_TRANSFER", label: t.paymentBank },
                  { value: "CARD", label: t.paymentCard },
                  { value: "OTHER", label: t.paymentOther },
                ]}
              />
            </label>
          </div>

          <label><span className={label}>{t.comment}</span><textarea className={`${input} min-h-24 resize-y`} maxLength={1200} value={form.comment} onChange={(e) => patch("comment", e.target.value)} /></label>

          <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-[13px] text-blue-200">{t.ttl}</div>
          {error && <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm font-bold text-red-200">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <button type="button" disabled={busy} onClick={onClose} className="rounded-2xl border border-slate-700 bg-slate-800 px-4 py-3 font-black text-slate-200">{t.cancel}</button>
            <button type="submit" disabled={busy} className="flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 font-black text-white disabled:opacity-60">
              {busy && <Loader2 size={18} className="animate-spin" />}
              {busy ? (editing ? t.saving : t.publishing) : (editing ? t.save : t.publish)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DriverMyFreights({
  open,
  onClose,
  driverToken,
  onChanged,
  createRequestKey = "",
  createContext = null,
  onCreated = null,
  onTargetCreateCancelled = null,
}) {
  const lang = languageCode();
  const t = COPY[lang] || COPY.ru;
  const [tab, setTab] = useState("ACTIVE");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [closingId, setClosingId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingItem, setEditingItem] = useState(null);
  const handledCreateRequestRef = useRef("");

  const auth = useMemo(() => bearer(driverToken), [driverToken]);

  const load = useCallback(async () => {
    if (!auth) {
      setItems([]);
      setError(t.loadError);
      return;
    }

    setBusy(true);
    setError("");
    try {
      const response = await fetch(`/api/driver/own-freights?state=${encodeURIComponent(tab)}&limit=100`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: { Authorization: auth },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(text(data?.error || data?.message || `HTTP ${response.status}`));
      const rows = Array.isArray(data?.items) ? data.items : [];
      setItems(rows.slice().sort((a, b) => itemTime(b) - itemTime(a)));
    } catch (err) {
      console.error("[driver-my-freights/load]", err);
      setItems([]);
      setError(t.loadError);
    } finally {
      setBusy(false);
    }
  }, [auth, tab, t.loadError]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  useEffect(() => {
    const key = text(createRequestKey);
    if (!open || !key || handledCreateRequestRef.current === key) return;
    handledCreateRequestRef.current = key;
    setEditingItem(null);
    setModalMode("create");
    setModalOpen(true);
  }, [open, createRequestKey]);

  if (!open) return null;

  const openCreate = () => {
    setEditingItem(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setModalMode("edit");
    setModalOpen(true);
  };

  const openRepeat = (item) => {
    setEditingItem(item);
    setModalMode("repeat");
    setModalOpen(true);
  };

  const afterSaved = async (post, meta = {}) => {
    setModalOpen(false);
    setEditingItem(null);
    onChanged?.();
    if (meta?.mode === "create" && createContext?.id) {
      await onCreated?.(post, meta);
      return;
    }
    await load();
  };

  const closeFreight = async (item) => {
    const id = text(item?.id);
    if (!id || closingId) return;
    if (!window.confirm(t.closeConfirm)) return;

    setClosingId(id);
    setError("");
    try {
      const response = await fetch(`/api/driver/own-freights/${encodeURIComponent(id)}/close`, {
        method: "POST",
        credentials: "include",
        headers: { Authorization: auth },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(text(data?.error || data?.message || `HTTP ${response.status}`));
      onChanged?.();
      await load();
    } catch (err) {
      console.error("[driver-my-freights/close]", err);
      setError(t.closeError);
    } finally {
      setClosingId("");
    }
  };

  const statusLabel = (item) => {
    const status = text(item?.status).toUpperCase();
    if (status === "EXPIRED") return t.expired;
    if (status === "BLOCKED") return t.blocked;
    if (status === "CLOSED") return t.closed;
    return status;
  };

  return (
    <div className="fixed inset-0 z-[10050] overflow-y-auto bg-slate-950 text-white"
        data-reisor-theme-surface="driver-my-freights">
      <div className="mx-auto min-h-full w-full max-w-[820px] pb-[max(32px,env(safe-area-inset-bottom))]">
        <div className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/95 px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))] backdrop-blur">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <button type="button" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900" aria-label={t.back}><ArrowLeft size={21} /></button>
              <div className="min-w-0">
                <h2 className="truncate text-xl font-black">{t.title}</h2>
                <p className="truncate text-[13px] text-slate-400">{t.ttl}</p>
              </div>
            </div>
            <button type="button" onClick={openCreate} className="flex shrink-0 items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-black"><Plus size={18} />{t.add}</button>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 rounded-2xl bg-slate-900 p-1">
            {["ACTIVE", "HISTORY"].map((value) => (
              <button key={value} type="button" onClick={() => setTab(value)} className={`rounded-xl px-3 py-2 text-sm font-black ${tab === value ? "bg-blue-600 text-white" : "text-slate-400"}`}>{value === "ACTIVE" ? t.active : t.history}</button>
            ))}
          </div>
        </div>

        <div className="space-y-3 p-4">
          <div className="flex justify-end">
            <button type="button" onClick={load} disabled={busy} className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-[13px] font-bold text-slate-300 disabled:opacity-50"><RefreshCw size={15} className={busy ? "animate-spin" : ""} />{t.refresh}</button>
          </div>

          {error && <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-sm font-bold text-red-200">{error}</div>}

          {busy && !items.length ? (
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/70 p-8 text-sm text-slate-400"><Loader2 size={20} className="animate-spin" />{t.loading}</div>
          ) : !items.length ? (
            <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/60 p-8 text-center text-sm text-slate-400">{tab === "ACTIVE" ? t.emptyActive : t.emptyHistory}</div>
          ) : (
            items.map((item) => {
              const isActive = text(item?.status).toUpperCase() === "ACTIVE";
              const price = item?.priceMode === "FIXED" ? formatAmount(item?.priceAmount, item?.currency, lang) : t.negotiable;
              return (
                <article key={text(item?.id)} className="rounded-3xl border border-slate-800 bg-slate-900/80 p-4 shadow-lg">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-base font-black"><Package size={19} className="shrink-0 text-blue-400" /><span className="truncate">{text(item?.cargo) || "—"}</span></div>
                      <div className="mt-1 flex items-center gap-2 text-sm font-bold text-slate-200"><span className="truncate">{text(item?.from) || "—"}</span><span className="text-slate-500">→</span><span className="truncate">{text(item?.to) || "—"}</span></div>
                    </div>
                    <div className="shrink-0 text-right text-sm font-black text-emerald-300">{price}</div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-[13px] text-slate-300 sm:grid-cols-4">
                    <div className="rounded-xl bg-slate-950/70 p-2"><div className="text-slate-500">{t.weight}</div><div className="mt-0.5 font-black">{Number(item?.weightTons || 0)} {t.tons}</div></div>
                    <div className="rounded-xl bg-slate-950/70 p-2"><div className="text-slate-500">{t.volume}</div><div className="mt-0.5 font-black">{item?.volumeM3 == null ? "—" : `${Number(item.volumeM3)} ${t.cubic}`}</div></div>
                    <div className="rounded-xl bg-slate-950/70 p-2"><div className="text-slate-500">{t.trucks}</div><div className="mt-0.5 flex items-center gap-1 font-black"><Truck size={13} />{Number(item?.requiredTrucks || 1)}</div></div>
                    <div className="rounded-xl bg-slate-950/70 p-2"><div className="text-slate-500">{t.loadingDate}</div><div className="mt-0.5 flex items-center gap-1 font-black"><CalendarDays size={13} />{item?.loadingReady ? t.ready : formatDateOnly(item?.loadingDate, lang)}</div></div>
                  </div>

                  {Array.isArray(item?.truckTypes) && item.truckTypes.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{item.truckTypes.map((type) => <span key={type} className="rounded-lg border border-slate-700 bg-slate-950/60 px-2 py-1 text-[13px] font-bold text-slate-300">{vehicleTypeLabel(normalizeTruckType(type), lang) || type}</span>)}</div>}

                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-800 pt-3 text-[13px] text-slate-400">
                    <span>{isActive ? `${t.expires}: ${formatDateTime(item?.expiresAt, lang)}` : `${statusLabel(item)} · ${formatDateTime(item?.closedAt || item?.expiresAt || item?.updatedAt, lang)}`}</span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {isActive ? (
                      <>
                        <button type="button" onClick={() => openEdit(item)} className="flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm font-black"><Pencil size={16} />{t.edit}</button>
                        <button type="button" disabled={closingId === text(item?.id)} onClick={() => closeFreight(item)} className="flex items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm font-black text-red-200 disabled:opacity-50">{closingId === text(item?.id) ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}{closingId === text(item?.id) ? t.closing : t.close}</button>
                      </>
                    ) : (
                      <button type="button" onClick={() => openRepeat(item)} className="col-span-2 flex items-center justify-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2.5 text-sm font-black text-blue-200"><Copy size={17} />{t.repeat}</button>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>

      <FreightModal
        open={modalOpen}
        onClose={() => {
          if (closingId) return;
          const targetedCreate = modalMode === "create" && Boolean(createContext?.id);
          setModalOpen(false);
          if (targetedCreate) onTargetCreateCancelled?.();
        }}
        driverToken={driverToken}
        item={editingItem}
        mode={modalMode}
        onSaved={afterSaved}
        targetLabel={modalMode === "create" ? text(createContext?.label) : ""}
      />
    </div>
  );
}
